/**
 * Level3Scene - 抛物线运动教学场景
 * @description 让玩家理解抛物线运动（水平速度 + 垂直加速度），通过设置初速度和发射角度使火柴人落入目标区域
 */

import Phaser from 'phaser';
import { PhysicsWorld, Vec2 } from '@/physics/PhysicsWorld.ts';
import { Stickman } from '@/characters/Stickman.ts';
import type { ProjectileConfig } from '@/levels/index.ts';
import { getGameState } from '@/core/GameState.ts';

const GAME_WIDTH = 800;
const GAME_HEIGHT = 600;

/** 默认抛射配置 */
const DEFAULT_PROJECTILE_CONFIG: ProjectileConfig = {
  enabled: true,
  minVelocity: 5,
  maxVelocity: 20,
  minAngle: 0,
  maxAngle: 90,
  defaultVelocity: 12,
  defaultAngle: 45,
  showTrajectory: true,
  trajectorySteps: 50,
};

/** 关卡配置 */
interface Level3Config {
  projectile: ProjectileConfig;
  startPosition: { x: number; y: number };
  goalPosition: { x: number; y: number };
  goalSize: { width: number; height: number };
  gravity: number;
}

/** 默认关卡配置 */
const DEFAULT_LEVEL3_CONFIG: Level3Config = {
  projectile: DEFAULT_PROJECTILE_CONFIG,
  startPosition: { x: 80, y: GAME_HEIGHT - 100 },
  goalPosition: { x: 650, y: GAME_HEIGHT - 50 },
  goalSize: { width: 120, height: 80 },
  gravity: 10,
};

/**
 * Level3Scene 类
 * 抛物线运动教学场景，玩家通过设置初速度和发射角度学习抛物线运动
 */
export class Level3Scene extends Phaser.Scene {
  private physicsWorld!: PhysicsWorld;
  private stickman!: Stickman;
  private config: Level3Config;

  // UI 元素
  private velocitySlider!: Phaser.GameObjects.DOMElement;
  private angleSlider!: Phaser.GameObjects.DOMElement;
  private launchButton!: Phaser.GameObjects.DOMElement;
  private trajectoryGraphics!: Phaser.GameObjects.Graphics;

  // 游戏状态
  private isLaunched: boolean = false;
  private currentVelocity: number = 12;
  private currentAngle: number = 45;
  private hasWon: boolean = false;
  private hasLanded: boolean = false;
  private elapsedTime: number = 0;

  constructor() {
    super({ key: 'Level3Scene' });
    this.config = DEFAULT_LEVEL3_CONFIG;
  }

  preload(): void {}

  create(): void {
    this.currentVelocity = this.config.projectile.defaultVelocity ?? 12;
    this.currentAngle = this.config.projectile.defaultAngle ?? 45;

    this.createPhysicsWorld();
    this.createPlatforms();
    this.createStickman();
    this.createGoalZone();
    this.createUI();
    this.createFormulaDisplay();
    this.createInstructions();
    this.updateTrajectoryPreview();
  }

  override update(_time: number, delta: number): void {
    if (this.isLaunched && !this.hasWon) {
      this.elapsedTime += delta / 1000;
      this.physicsWorld.step(delta / 1000);
      this.stickman.update();
      this.checkWinCondition();
      this.checkOutOfBounds();
    }
  }

  private createPhysicsWorld(): void {
    this.physicsWorld = new PhysicsWorld({
      gravity: Vec2(0, -this.config.gravity),
      allowSleep: true,
    });

    // 创建边界
    this.physicsWorld.createBody('boundary', {
      x: GAME_WIDTH / 2,
      y: 0,
      type: 'static',
      isBoundary: true,
      boundarySize: { width: GAME_WIDTH, height: GAME_HEIGHT },
    });
  }

  private createPlatforms(): void {
    // 发射平台
    const launchPlatformGraphics = this.add.graphics();
    launchPlatformGraphics.fillStyle(0x4a4a6a, 1);
    launchPlatformGraphics.fillRect(
      this.config.startPosition.x - 30,
      this.config.startPosition.y - 20,
      60,
      20
    );

    // 障碍物 1
    const obstacle1Graphics = this.add.graphics();
    obstacle1Graphics.fillStyle(0x686868, 1);
    obstacle1Graphics.fillRect(270, GAME_HEIGHT - 250, 30, 80);

    // 障碍物 2
    const obstacle2Graphics = this.add.graphics();
    obstacle2Graphics.fillStyle(0x686868, 1);
    obstacle2Graphics.fillRect(470, GAME_HEIGHT - 150, 30, 60);

    // 地面
    const groundGraphics = this.add.graphics();
    groundGraphics.fillStyle(0x4a4a6a, 1);
    groundGraphics.fillRect(0, GAME_HEIGHT - 40, GAME_WIDTH, 40);
  }

  private createStickman(): void {
    this.stickman = new Stickman(
      this,
      this.physicsWorld,
      {
        x: this.config.startPosition.x,
        y: this.config.startPosition.y,
        scale: 0.8,
        colors: {
          line: 0xffffff,
          headFill: 0x1a1a2e,
          lineWidth: 2,
        },
      },
      GAME_HEIGHT
    );
  }

  private createGoalZone(): void {
    const { goalPosition, goalSize } = this.config;

    const goalGraphics = this.add.graphics();
    goalGraphics.fillStyle(0x4ade80, 0.3);
    goalGraphics.fillRect(
      goalPosition.x - goalSize.width / 2,
      goalPosition.y - goalSize.height,
      goalSize.width,
      goalSize.height
    );
    goalGraphics.lineStyle(3, 0x4ade80);
    goalGraphics.strokeRect(
      goalPosition.x - goalSize.width / 2,
      goalPosition.y - goalSize.height,
      goalSize.width,
      goalSize.height
    );

    // 目标标签
    this.add.text(
      goalPosition.x - goalSize.width / 2,
      goalPosition.y - goalSize.height - 25,
      '目标区域',
      {
        fontSize: '14px',
        color: '#4ade80',
        fontFamily: 'Arial',
      }
    );
  }

  private createUI(): void {
    // 轨迹预览图形
    this.trajectoryGraphics = this.add.graphics();

    // 速度滑块
    this.velocitySlider = this.add.dom(150, 30).createFromHTML(`
      <div style="display: flex; align-items: center; gap: 10px;">
        <label style="color: #ffffff; font-size: 14px;">初速度 (v₀):</label>
        <input type="range" id="velocity-slider" 
               min="${this.config.projectile.minVelocity}" 
               max="${this.config.projectile.maxVelocity}" 
               value="${this.currentVelocity}"
               style="width: 100px;">
        <span id="velocity-value" style="color: #ffd700; font-size: 14px; width: 40px;">${this.currentVelocity}</span>
        <span style="color: #888888; font-size: 12px;">单位/秒</span>
      </div>
    `);

    const velocitySliderElement = this.velocitySlider.node.querySelector('#velocity-slider') as HTMLInputElement;
    velocitySliderElement.addEventListener('input', (e) => {
      const target = e.target as HTMLInputElement;
      this.currentVelocity = parseFloat(target.value);
      this.updateVelocityDisplay();
      this.updateTrajectoryPreview();
    });

    // 角度滑块
    this.angleSlider = this.add.dom(400, 30).createFromHTML(`
      <div style="display: flex; align-items: center; gap: 10px;">
        <label style="color: #ffffff; font-size: 14px;">发射角度 (θ):</label>
        <input type="range" id="angle-slider" 
               min="${this.config.projectile.minAngle}" 
               max="${this.config.projectile.maxAngle}" 
               value="${this.currentAngle}"
               style="width: 100px;">
        <span id="angle-value" style="color: #ffd700; font-size: 14px; width: 40px;">${this.currentAngle}°</span>
      </div>
    `);

    const angleSliderElement = this.angleSlider.node.querySelector('#angle-slider') as HTMLInputElement;
    angleSliderElement.addEventListener('input', (e) => {
      const target = e.target as HTMLInputElement;
      this.currentAngle = parseFloat(target.value);
      this.updateAngleDisplay();
      this.updateTrajectoryPreview();
    });

    // 发射按钮
    this.launchButton = this.add.dom(600, 30).createFromHTML(`
      <button id="launch-btn" 
              style="padding: 10px 20px; background: #4ade80; color: #1a1a2e; 
                     border: none; border-radius: 4px; font-size: 14px; cursor: pointer;">
        发射
      </button>
    `);

    const launchBtnElement = this.launchButton.node.querySelector('#launch-btn') as HTMLButtonElement;
    launchBtnElement.addEventListener('click', () => this.launchProjectile());

    // 重置按钮
    const resetButton = this.add.dom(700, 30).createFromHTML(`
      <button id="reset-btn" 
              style="padding: 10px 20px; background: #ff6b6b; color: #ffffff; 
                     border: none; border-radius: 4px; font-size: 14px; cursor: pointer;">
        重置
      </button>
    `);

    const resetBtnElement = resetButton.node.querySelector('#reset-btn') as HTMLButtonElement;
    resetBtnElement.addEventListener('click', () => this.resetSimulation());
  }

  private createFormulaDisplay(): void {
    // 主公式
    const formulaText = this.add.text(
      GAME_WIDTH / 2,
      GAME_HEIGHT - 80,
      '抛物线运动公式',
      {
        fontSize: '18px',
        color: '#ffd700',
        fontFamily: 'Arial',
        align: 'center',
      }
    );
    formulaText.setOrigin(0.5, 0);

    // x 方向公式
    this.add.text(
      GAME_WIDTH / 2 - 150,
      GAME_HEIGHT - 55,
      'x = v₀·cos(θ)·t',
      {
        fontSize: '14px',
        color: '#ffffff',
        fontFamily: 'Arial',
      }
    );

    // y 方向公式
    this.add.text(
      GAME_WIDTH / 2 + 50,
      GAME_HEIGHT - 55,
      'y = v₀·sin(θ)·t - ½gt²',
      {
        fontSize: '14px',
        color: '#ffffff',
        fontFamily: 'Arial',
      }
    );

    // 变量说明
    this.add.text(
      GAME_WIDTH / 2,
      GAME_HEIGHT - 30,
      'v₀: 初速度, θ: 发射角度, t: 时间, g: 重力加速度',
      {
        fontSize: '12px',
        color: '#888888',
        fontFamily: 'Arial',
        align: 'center',
      }
    ).setOrigin(0.5, 0);
  }

  private createInstructions(): void {
    this.add.text(
      10,
      80,
      '【教学目标】理解抛物线运动\n' +
      '抛物线运动由水平匀速运动和垂直匀加速运动组成\n' +
      '调整初速度和发射角度，让火柴人落入目标区域！',
      {
        fontSize: '14px',
        color: '#888888',
        fontFamily: 'Arial',
        wordWrap: { width: 300 },
      }
    );
  }

  private updateVelocityDisplay(): void {
    const valueSpan = this.velocitySlider.node.querySelector('#velocity-value') as HTMLSpanElement;
    if (valueSpan) {
      valueSpan.textContent = this.currentVelocity.toFixed(0);
    }
  }

  private updateAngleDisplay(): void {
    const valueSpan = this.angleSlider.node.querySelector('#angle-value') as HTMLSpanElement;
    if (valueSpan) {
      valueSpan.textContent = `${this.currentAngle.toFixed(0)}°`;
    }
  }

  private updateTrajectoryPreview(): void {
    if (!this.config.projectile.showTrajectory || this.isLaunched) return;

    this.trajectoryGraphics.clear();
    this.trajectoryGraphics.lineStyle(2, 0xffd700, 0.5);

    const points = this.calculateTrajectory(
      this.currentVelocity,
      this.currentAngle,
      this.config.projectile.trajectorySteps ?? 50
    );

    if (points.length > 0) {
      const firstPoint = points[0]!;
      this.trajectoryGraphics.beginPath();
      this.trajectoryGraphics.moveTo(firstPoint.x, firstPoint.y);
      for (let i = 1; i < points.length; i++) {
        const point = points[i]!;
        this.trajectoryGraphics.lineTo(point.x, point.y);
      }
      this.trajectoryGraphics.strokePath();
    }
  }

  private calculateTrajectory(
    velocity: number,
    angleDegrees: number,
    steps: number
  ): { x: number; y: number }[] {
    const points: { x: number; y: number }[] = [];
    const angleRadians = (angleDegrees * Math.PI) / 180;
    const vx = velocity * Math.cos(angleRadians);
    const vy = velocity * Math.sin(angleRadians);
    const g = this.config.gravity;
    const startX = this.config.startPosition.x;
    const startY = this.config.startPosition.y;
    const dt = 0.05; // 时间步长

    for (let i = 0; i < steps; i++) {
      const t = i * dt;
      const x = startX + vx * t * 5; // 缩放因子
      const y = startY - (vy * t * 5 - 0.5 * g * t * t * 25);

      // 停止绘制如果超出屏幕或触地
      if (y > GAME_HEIGHT - 40 || x > GAME_WIDTH) break;

      points.push({ x, y });
    }

    return points;
  }

  private launchProjectile(): void {
    if (this.isLaunched || this.hasWon) return;

    this.isLaunched = true;
    this.hasLanded = false;
    this.elapsedTime = 0;

    // 清除轨迹预览
    this.trajectoryGraphics.clear();

    // 计算初始速度分量
    const angleRadians = (this.currentAngle * Math.PI) / 180;
    const vx = this.currentVelocity * Math.cos(angleRadians) * 5; // 缩放因子
    const vy = this.currentVelocity * Math.sin(angleRadians) * 5;

    // 设置火柴人初始速度
    const torso = this.stickman.getBody().getBody('torso');
    if (torso) {
      torso.setLinearVelocity(Vec2(vx, vy));
      torso.setAwake(true);
    }

    // 更新按钮状态
    const launchBtnElement = this.launchButton.node.querySelector('#launch-btn') as HTMLButtonElement;
    launchBtnElement.textContent = '飞行中...';
    launchBtnElement.style.background = '#888888';
    launchBtnElement.disabled = true;
  }

  private resetSimulation(): void {
    this.isLaunched = false;
    this.hasWon = false;
    this.hasLanded = false;
    this.elapsedTime = 0;

    // 重新创建物理世界和火柴人
    this.physicsWorld.clear();
    this.stickman.destroy();
    this.createPhysicsWorld();
    this.createPlatforms();
    this.createStickman();

    // 重置滑块值
    this.currentVelocity = this.config.projectile.defaultVelocity ?? 12;
    this.currentAngle = this.config.projectile.defaultAngle ?? 45;

    const velocitySliderElement = this.velocitySlider.node.querySelector('#velocity-slider') as HTMLInputElement;
    const angleSliderElement = this.angleSlider.node.querySelector('#angle-slider') as HTMLInputElement;
    velocitySliderElement.value = this.currentVelocity.toString();
    angleSliderElement.value = this.currentAngle.toString();
    this.updateVelocityDisplay();
    this.updateAngleDisplay();

    // 重新显示轨迹预览
    this.updateTrajectoryPreview();

    // 重置按钮状态
    const launchBtnElement = this.launchButton.node.querySelector('#launch-btn') as HTMLButtonElement;
    launchBtnElement.textContent = '发射';
    launchBtnElement.style.background = '#4ade80';
    launchBtnElement.disabled = false;

    // 移除结果消息
    this.children.each((child) => {
      if (child.name === 'resultText' || child.name === 'resultSubtext') {
        child.destroy();
      }
    });
  }

  private checkWinCondition(): void {
    if (this.hasWon || this.hasLanded) return;

    const stickmanPos = this.stickman.getPosition();
    const { goalPosition, goalSize } = this.config;

    // 检查是否在目标区域内
    const inGoalX = Math.abs(stickmanPos.x - goalPosition.x) < goalSize.width / 2;
    const inGoalY =
      stickmanPos.y > goalPosition.y - goalSize.height &&
      stickmanPos.y < goalPosition.y;

    // 检查是否落地（速度接近零）
    const torso = this.stickman.getBody().getBody('torso');
    if (torso) {
      const velocity = torso.getLinearVelocity();
      const isLanded = Math.abs(velocity.y) < 1 && stickmanPos.y > GAME_HEIGHT - 60;

      if (inGoalX && inGoalY && isLanded) {
        this.onWin();
      } else if (isLanded && !inGoalX || !inGoalY) {
        this.onLose();
      }
    }
  }

  private checkOutOfBounds(): void {
    if (this.hasWon || this.hasLanded) return;

    const stickmanPos = this.stickman.getPosition();

    // 检查是否超出屏幕边界
    if (
      stickmanPos.x < 0 ||
      stickmanPos.x > GAME_WIDTH ||
      stickmanPos.y < 0 ||
      stickmanPos.y > GAME_HEIGHT
    ) {
      this.onLose();
    }
  }

  private onWin(): void {
    this.hasWon = true;
    this.isLaunched = false;

    const gameState = getGameState();
    gameState.completeLevel(3, this.elapsedTime);

    this.add
      .text(
        GAME_WIDTH / 2,
        GAME_HEIGHT / 2 - 50,
        '🎉 恭喜通关！',
        {
          fontSize: '28px',
          color: '#4ade80',
          fontFamily: 'Arial',
          align: 'center',
        }
      )
      .setOrigin(0.5, 0.5)
      .setName('resultText');

    this.add
      .text(
        GAME_WIDTH / 2,
        GAME_HEIGHT / 2,
        `初速度: ${this.currentVelocity} 单位/秒\n发射角度: ${this.currentAngle}°`,
        {
          fontSize: '16px',
          color: '#888888',
          fontFamily: 'Arial',
          align: 'center',
        }
      )
      .setOrigin(0.5, 0.5)
      .setName('resultSubtext');

    this.createLevel3CompletionButtons();
  }

  private createLevel3CompletionButtons(): void {
    const gameState = getGameState();

    if (gameState.isLevelUnlocked(4)) {
      const nextBtn = this.add.text(GAME_WIDTH / 2 - 80, GAME_HEIGHT / 2 + 50, '下一关', {
        fontSize: '18px',
        color: '#4ade80',
        fontFamily: 'Arial',
        backgroundColor: '#333333',
        padding: { x: 15, y: 8 },
      }).setOrigin(0.5).setInteractive();

      nextBtn.on('pointerdown', () => {
        this.scene.start('Level4Scene');
      });
    }

    const backBtn = this.add.text(GAME_WIDTH / 2 + 80, GAME_HEIGHT / 2 + 50, '返回', {
      fontSize: '18px',
      color: '#888888',
      fontFamily: 'Arial',
      backgroundColor: '#333333',
      padding: { x: 15, y: 8 },
    }).setOrigin(0.5).setInteractive();

    backBtn.on('pointerdown', () => {
      this.scene.start('LevelSelectScene');
    });
  }

  private onLose(): void {
    this.hasLanded = true;
    this.isLaunched = false;

    const stickmanPos = this.stickman.getPosition();
    const distanceToGoal = Math.abs(stickmanPos.x - this.config.goalPosition.x);

    // 显示失败消息
    this.add
      .text(
        GAME_WIDTH / 2,
        GAME_HEIGHT / 2 - 50,
        '未落入目标区域！',
        {
          fontSize: '24px',
          color: '#ff6b6b',
          fontFamily: 'Arial',
          align: 'center',
        }
      )
      .setOrigin(0.5, 0.5)
      .setName('resultText');

    // 显示提示
    this.add
      .text(
        GAME_WIDTH / 2,
        GAME_HEIGHT / 2,
        `距离目标: ${distanceToGoal.toFixed(0)} 单位\n请调整参数重新尝试`,
        {
          fontSize: '14px',
          color: '#888888',
          fontFamily: 'Arial',
          align: 'center',
        }
      )
      .setOrigin(0.5, 0.5)
      .setName('resultSubtext');
  }

  /** 获取物理世界实例（用于测试） */
  getPhysicsWorld(): PhysicsWorld {
    return this.physicsWorld;
  }

  /** 获取火柴人实例（用于测试） */
  getStickman(): Stickman {
    return this.stickman;
  }

  /** 获取当前游戏状态（用于测试） */
  getGameState(): {
    isLaunched: boolean;
    currentVelocity: number;
    currentAngle: number;
    hasWon: boolean;
    hasLanded: boolean;
  } {
    return {
      isLaunched: this.isLaunched,
      currentVelocity: this.currentVelocity,
      currentAngle: this.currentAngle,
      hasWon: this.hasWon,
      hasLanded: this.hasLanded,
    };
  }

  /** 计算抛物线轨迹（用于测试） */
  calculateTrajectoryPoints(
    velocity: number,
    angleDegrees: number
  ): { x: number; y: number }[] {
    return this.calculateTrajectory(velocity, angleDegrees, 50);
  }
}